import { message } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';

// Formatos de arquivo permitidos
export const ALLOWED_FILE_FORMATS = {
  PDF: 'application/pdf',
  DOC: 'application/msword',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  TXT: 'text/plain'
};

// Formatos de imagem permitidos para avatar
export const ALLOWED_IMAGE_FORMATS = {
  JPEG: 'image/jpeg',
  JPG: 'image/jpg',
  PNG: 'image/png',
  WEBP: 'image/webp'
};

// Extensões permitidas
export const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt'];

// Extensões de imagem permitidas
export const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

// String para o atributo accept do input
export const FILE_ACCEPT_STRING = '.pdf,.doc,.docx,.txt';

// String para o atributo accept de imagens
export const IMAGE_ACCEPT_STRING = '.jpg,.jpeg,.png,.webp';

/**
 * Valida se o arquivo está nos formatos permitidos
 * @param file - Arquivo a ser validado
 * @returns boolean - true se válido, false se inválido
 */
export const validateFileFormat = (file: File | UploadFile): boolean => {
  const fileName = file.name.toLowerCase();
  const fileType = file.type;

  // Verifica por extensão
  const hasValidExtension = ALLOWED_FILE_EXTENSIONS.some(ext => 
    fileName.endsWith(ext)
  );

  // Verifica por MIME type (quando disponível)
  const hasValidMimeType = Boolean(fileType && Object.values(ALLOWED_FILE_FORMATS).includes(fileType));

  // Arquivo é válido se tem extensão válida OU MIME type válido
  const isValid = hasValidExtension || hasValidMimeType;

  if (!isValid) {
    message.error('Only PDF, DOC, DOCX, and TXT files are allowed.');
  }

  return isValid;
};

/**
 * Valida se a imagem está nos formatos permitidos
 * @param file - Arquivo de imagem a ser validado
 * @returns boolean - true se válido, false se inválido
 */
export const validateImageFormat = (file: File | UploadFile): boolean => {
  const fileName = file.name.toLowerCase();
  const fileType = file.type;

  // Verifica por extensão
  const hasValidExtension = ALLOWED_IMAGE_EXTENSIONS.some(ext => 
    fileName.endsWith(ext)
  );

  // Verifica por MIME type (quando disponível)
  const hasValidMimeType = Boolean(fileType && Object.values(ALLOWED_IMAGE_FORMATS).includes(fileType));

  // Arquivo é válido se tem extensão válida OU MIME type válido
  const isValid = hasValidExtension || hasValidMimeType;

  if (!isValid) {
    message.error('Only JPG, JPEG, PNG, and WEBP images are allowed.');
  }

  return isValid;
};

/**
 * Handler para beforeUpload que valida formato e previne upload automático
 * @param file - Arquivo a ser validado
 * @returns boolean - sempre false para prevenir upload automático
 */
export const validateAndPreventUpload = (file: File | UploadFile): boolean => {
  validateFileFormat(file);
  return false; // Previne upload automático
}; 